import { useMemo, useState } from 'react';
import useSWR from 'swr';
import {
  Loader2, TrendingUp, ShoppingBag, Repeat, MapPin, Award,
  Gift, ExternalLink, Settings, Save, BarChart3, FileDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { API, fetchAdminAnalytics, fetchAdminSettings, updateAdminSettings } from '@/lib/api';

const AUD = (n) =>
  `$${(Number(n) || 0).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const AUD2 = (n) =>
  `$${(Number(n) || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const WINDOWS = [
  { key: 7,   label: '7d'  },
  { key: 30,  label: '30d' },
  { key: 90,  label: '90d' },
  { key: 180, label: '6mo' },
  { key: 365, label: '1y'  },
];

const CATEGORY_COLOR = {
  beef:       '#9B111E',
  lamb:       '#C2410C',
  poultry:    '#B45309',
  wagyu:      '#7C2D12',
  smallgoods: '#059669',
  jerky:      '#0369A1',
  seafood:    '#0891B2',
  pork:       '#DB2777',
  other:      '#525252',
};

function Card({ title, subtitle, right, children, className = '' }) {
  return (
    <div className={`border border-white/10 bg-white/[0.02] p-5 sm:p-6 ${className}`}>
      <div className="flex items-baseline justify-between gap-4 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-white/45">{title}</div>
          {subtitle && <div className="mt-1.5 font-display text-lg sm:text-xl">{subtitle}</div>}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="border border-white/10 p-4">
      <div className="text-[10px] uppercase tracking-[0.24em] text-white/45">{label}</div>
      <div className="mt-2 font-display text-2xl sm:text-3xl text-white tabular-nums leading-none">{value}</div>
      {sub && <div className="mt-1.5 text-xs text-white/50">{sub}</div>}
    </div>
  );
}

/* --- Simple SVG chart primitives (no chart lib to keep bundle lean) --- */

function CategoryDonut({ data }) {
  const total = useMemo(() => data.reduce((s, d) => s + (d.revenue || 0), 0), [data]);
  if (!total) return <div className="text-white/40 text-sm py-8 text-center">No revenue yet.</div>;

  const R = 60;
  const C = 2 * Math.PI * R;
  let offset = 0;

  return (
    <div className="flex items-center gap-6 flex-wrap">
      <svg viewBox="0 0 160 160" className="w-40 h-40 shrink-0">
        {data.map((d) => {
          const frac = d.revenue / total;
          const dash = frac * C;
          const el = (
            <circle
              key={d.category}
              cx="80" cy="80" r={R}
              fill="none"
              stroke={CATEGORY_COLOR[d.category] || CATEGORY_COLOR.other}
              strokeWidth="24"
              strokeDasharray={`${dash} ${C - dash}`}
              strokeDashoffset={-offset}
              transform="rotate(-90 80 80)"
            />
          );
          offset += dash;
          return el;
        })}
        <text x="80" y="76" textAnchor="middle" className="fill-white/80" style={{ font: 'italic 300 22px/1 Cormorant Garamond, serif' }}>
          {AUD(total)}
        </text>
        <text x="80" y="94" textAnchor="middle" className="fill-white/40" style={{ font: '9px/1 -apple-system,Helvetica,Arial,sans-serif', textTransform: 'uppercase', letterSpacing: '.24em' }}>
          total
        </text>
      </svg>
      <ul className="flex-1 min-w-0 space-y-2">
        {data.map((d) => {
          const pct = ((d.revenue / total) * 100).toFixed(1);
          return (
            <li key={d.category} className="flex items-center gap-3 text-sm">
              <span className="block w-2.5 h-2.5" style={{ background: CATEGORY_COLOR[d.category] || CATEGORY_COLOR.other }} />
              <span className="text-white/80 capitalize flex-1">{d.category}</span>
              <span className="text-white/50 tabular-nums text-xs w-14 text-right">{pct}%</span>
              <span className="text-white tabular-nums w-20 text-right">{AUD(d.revenue)}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function WeekdayBars({ data }) {
  const max = Math.max(1, ...data.map((d) => d.revenue));
  return (
    <div className="grid grid-cols-7 gap-2 items-end h-40">
      {data.map((d) => {
        const h = Math.max(3, (d.revenue / max) * 100);
        return (
          <div key={d.day} className="flex flex-col items-center gap-2 h-full">
            <div className="flex-1 w-full flex items-end">
              <div
                className="w-full bg-[var(--mw-primary)] hover:opacity-100 opacity-80 transition-opacity relative group"
                style={{ height: `${h}%` }}
              >
                <div className="absolute -top-8 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 pointer-events-none text-[10px] uppercase tracking-[0.2em] text-white bg-black/80 px-2 py-1 whitespace-nowrap">
                  {AUD(d.revenue)} · {d.orders}
                </div>
              </div>
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/50">{d.day}</div>
          </div>
        );
      })}
    </div>
  );
}

function FunnelStage({ label, value, of, tint }) {
  const pct = of > 0 ? ((value / of) * 100).toFixed(1) : '0.0';
  return (
    <div className="border border-white/10 p-4 flex items-center gap-4">
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-[0.24em] text-white/45">{label}</div>
        <div className="mt-1.5 font-display text-2xl text-white tabular-nums">{value}</div>
      </div>
      {of > 0 && (
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: tint }}>{pct}%</div>
          <div className="text-[9px] uppercase tracking-[0.22em] text-white/40">rate</div>
        </div>
      )}
    </div>
  );
}

/* --- Plausible embed --- */

function PlausibleEmbed({ sharedLink, onSave }) {
  const [draft, setDraft] = useState(sharedLink || '');
  const [editing, setEditing] = useState(!sharedLink);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const trimmed = (draft || '').trim();
      // Normalise: append embed=true if missing so Plausible renders without chrome
      let url = trimmed;
      if (url && !/[?&]embed=true/.test(url)) {
        url += (url.includes('?') ? '&' : '?') + 'embed=true';
      }
      if (url && !/[?&]theme=/.test(url)) url += '&theme=dark';
      await onSave(url);
      setEditing(false);
      toast.success(url ? 'Plausible link saved' : 'Plausible link cleared');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <Card
        title="Traffic · Plausible Analytics"
        subtitle="Paste your Plausible shared-link URL"
        right={
          <a
            href="https://plausible.io/themeatworks.com.au/settings/visibility"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] text-white/50 hover:text-white transition-colors"
          >
            Create link <ExternalLink size={10} />
          </a>
        }
      >
        <p className="text-sm text-white/60 leading-relaxed mb-4">
          In your Plausible dashboard → <strong>Site Settings → Visibility → Add a shared link</strong>{' '}
          (no password). Copy the URL that starts with{' '}
          <code className="text-white/80">https://plausible.io/share/…</code> and paste it below.
          You&apos;ll then see live visitors, top pages, referrers, countries and goal conversions right here.
        </p>
        <div className="flex gap-2 flex-wrap">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="https://plausible.io/share/themeatworks.com.au?auth=..."
            data-testid="admin-plausible-input"
            className="flex-1 min-w-[280px] bg-white/[0.03] border border-white/10 focus:border-white/25 outline-none px-3 py-2.5 text-sm text-white placeholder:text-white/30"
          />
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="admin-plausible-save"
            style={{ color: '#fff' }}
            className="inline-flex items-center gap-2 bg-[var(--mw-primary)] hover:bg-[var(--mw-primary-hover)] text-[11px] uppercase tracking-[0.22em] px-5 py-2.5 transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            Save
          </button>
        </div>
      </Card>
    );
  }

  return (
    <Card
      title="Traffic · Plausible Analytics"
      subtitle="Live visitors, pages, referrers, conversion goals"
      right={
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] text-white/50 hover:text-white transition-colors"
        >
          <Settings size={11} /> Change link
        </button>
      }
    >
      <div className="w-full border border-white/10 overflow-hidden">
        <iframe
          {...{ 'plausible-embed': '' }}
          src={sharedLink}
          scrolling="no"
          frameBorder="0"
          loading="lazy"
          title="Plausible Analytics"
          className="w-full block bg-[#111]"
          style={{ height: 1750, minHeight: 1750 }}
        />
      </div>
      <div className="mt-3 text-[10px] uppercase tracking-[0.22em] text-white/35">
        Powered by Plausible · privacy-friendly, cookieless analytics
      </div>
    </Card>
  );
}

/* --- Main panel --- */

export default function AnalyticsPanel() {
  const [days, setDays] = useState(90);
  const { data, isLoading } = useSWR(['admin-analytics', days], () => fetchAdminAnalytics(days));
  const { data: settings, mutate: mutateSettings } = useSWR('admin-settings', fetchAdminSettings);

  const onSavePlausible = async (url) => {
    await updateAdminSettings({ plausible_shared_link: url });
    mutateSettings();
  };

  if (isLoading && !data) {
    return (
      <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-white/40" /></div>
    );
  }

  const t = data?.totals || {};
  const subMix = t.subscription_revenue && t.revenue
    ? ((t.subscription_revenue / t.revenue) * 100).toFixed(1)
    : '0.0';

  const funnel = data?.funnel || {};

  return (
    <div data-testid="admin-analytics" className="space-y-10">
      {/* Header + window picker */}
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-white/55 flex items-center gap-2">
            <BarChart3 size={12} /> Website analytics
          </div>
          <h2 className="font-display font-light text-3xl sm:text-4xl mt-2">Deep insights</h2>
          <p className="text-white/55 text-sm mt-2 max-w-xl">
            Everything you need to answer &ldquo;what&apos;s working?&rdquo;. In-house metrics from paid orders +
            live traffic pulled from Plausible.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <a
            href={`${API}/admin/pricelist.pdf`}
            data-testid="admin-download-pricelist"
            title="Full internal pricelist — Cost / Export / Retail. Confidential."
            className="inline-flex items-center gap-2 border border-white/15 hover:border-[var(--mw-primary)] hover:text-[var(--mw-primary)] text-white/80 text-[11px] uppercase tracking-[0.22em] px-4 py-2 transition-colors"
          >
            <FileDown size={13} /> Pricelist · Confidential
          </a>
          <a
            href={`${API}/admin/pricelist.pdf?include_cost=false`}
            data-testid="admin-download-pricelist-client"
            title="Client-safe pricelist — Export / Retail only. Safe to share with buyers."
            className="inline-flex items-center gap-2 border border-white/15 hover:border-white hover:text-white text-white/80 text-[11px] uppercase tracking-[0.22em] px-4 py-2 transition-colors"
          >
            <FileDown size={13} /> Pricelist · Client
          </a>
          <a
            href={`${API}/admin/pricelist.pdf?mode=export`}
            data-testid="admin-download-pricelist-export"
            title="Export-only pricelist — Export prices in AUD/kg. Ideal for overseas buyers."
            className="inline-flex items-center gap-2 border border-white/15 hover:border-white hover:text-white text-white/80 text-[11px] uppercase tracking-[0.22em] px-4 py-2 transition-colors"
          >
            <FileDown size={13} /> Pricelist · Export
          </a>
          <div className="flex items-center gap-1 border border-white/10 p-1">
            {WINDOWS.map((w) => (
              <button
                key={w.key}
                type="button"
                onClick={() => setDays(w.key)}
                data-testid={`admin-analytics-window-${w.key}`}
                className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] transition-colors ${
                  days === w.key
                    ? 'bg-[var(--mw-primary)] text-white'
                    : 'text-white/60 hover:text-white'
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Top stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Stat label={`Revenue · ${days}d`}   value={AUD(t.revenue)}  sub={`${t.orders || 0} paid orders`} />
        <Stat label="Avg order value"        value={AUD2(t.aov)}     sub={t.items_sold ? `${Math.round(t.items_sold)} items sold` : '—'} />
        <Stat label="Subscription revenue"   value={AUD(t.subscription_revenue)} sub={`${subMix}% of revenue`} />
        <Stat label="One-off revenue"        value={AUD(t.one_off_revenue)}      sub={`${100 - Number(subMix)}% of revenue`} />
      </div>

      {/* Two-col: category donut + weekday bars */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Revenue by category" subtitle="Which range earns the most" right={<Award size={14} className="text-white/40" />}>
          <CategoryDonut data={data?.revenue_by_category || []} />
        </Card>
        <Card title="Revenue by weekday" subtitle="Best trading days" right={<TrendingUp size={14} className="text-white/40" />}>
          <WeekdayBars data={data?.revenue_by_weekday || []} />
        </Card>
      </div>

      {/* Two-col: geographic + funnel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Top suburbs" subtitle="Where Perth is buying" right={<MapPin size={14} className="text-white/40" />}>
          {(!data?.revenue_by_suburb || !data.revenue_by_suburb.length) ? (
            <div className="text-white/40 text-sm py-6">No delivery data yet.</div>
          ) : (
            <ol className="space-y-2.5">
              {data.revenue_by_suburb.map((s, i) => (
                <li key={s.suburb} className="flex items-center gap-3 text-sm">
                  <span className="font-display text-lg text-white/25 tabular-nums w-6">{i + 1}</span>
                  <span className="flex-1 text-white/85">{s.suburb}</span>
                  <span className="text-white/45 text-xs tabular-nums w-16 text-right">{s.orders} ord</span>
                  <span className="text-white tabular-nums w-20 text-right">{AUD(s.revenue)}</span>
                </li>
              ))}
            </ol>
          )}
        </Card>
        <Card title="Trade funnel" subtitle="Prospect → paying customer" right={<ShoppingBag size={14} className="text-white/40" />}>
          <div className="space-y-2.5">
            <FunnelStage label="Inquiries submitted"        value={funnel.inquiries_total || 0}    of={funnel.inquiries_total || 0} tint="#6EE7B7" />
            <FunnelStage label="Wholesale applications"     value={funnel.wholesale_total || 0}    of={funnel.inquiries_total || 0} tint="#93C5FD" />
            <FunnelStage label="  · approved"               value={funnel.wholesale_approved || 0} of={funnel.wholesale_total || 0} tint="#93C5FD" />
            <FunnelStage label="Orders started (any state)" value={funnel.orders_started || 0}     of={0}                             tint="#F59E0B" />
            <FunnelStage label="  · paid"                   value={funnel.orders_paid || 0}        of={funnel.orders_started || 0}    tint="#F59E0B" />
          </div>
        </Card>
      </div>

      {/* Two-col: top products + referrals */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Top products" subtitle={`Bestsellers · last ${days}d`} right={<Award size={14} className="text-white/40" />}>
          {(!data?.top_products || !data.top_products.length) ? (
            <div className="text-white/40 text-sm py-6">No paid orders in this window.</div>
          ) : (
            <ol className="space-y-2.5">
              {data.top_products.map((p, i) => (
                <li key={p.product_id} className="flex items-center gap-3 text-sm">
                  <span className="font-display text-lg text-white/25 tabular-nums w-6">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <div className="text-white/85 truncate">{p.name}</div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mt-0.5">{p.category} · {p.units} units</div>
                  </div>
                  <span className="text-white tabular-nums w-20 text-right">{AUD(p.revenue)}</span>
                </li>
              ))}
            </ol>
          )}
        </Card>
        <Card title="Referral leaders" subtitle="Codes driving revenue" right={<Gift size={14} className="text-white/40" />}>
          {(!data?.referral_leaders || !data.referral_leaders.length) ? (
            <div className="text-white/40 text-sm py-6">No referral-attributed orders yet.</div>
          ) : (
            <ol className="space-y-2.5">
              {data.referral_leaders.map((r, i) => (
                <li key={r.code} className="flex items-center gap-3 text-sm">
                  <span className="font-display text-lg text-white/25 tabular-nums w-6">{i + 1}</span>
                  <span className="flex-1 font-mono text-white/85 text-xs">{r.code}</span>
                  <span className="text-white/45 text-xs tabular-nums w-16 text-right">{r.orders} ord</span>
                  <span className="text-white tabular-nums w-20 text-right">{AUD(r.revenue)}</span>
                </li>
              ))}
            </ol>
          )}
        </Card>
      </div>

      {/* Plausible embed */}
      <PlausibleEmbed
        sharedLink={settings?.plausible_shared_link || ''}
        onSave={onSavePlausible}
      />

      {/* Sub mix bar (footer factoid) */}
      <div className="border border-white/10 bg-white/[0.02] p-5 sm:p-6">
        <div className="flex items-baseline justify-between gap-4 mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-white/45">Subscription vs one-off</div>
            <div className="mt-1 font-display text-lg">Revenue mix · last {days}d</div>
          </div>
          <Repeat size={14} className="text-white/40" />
        </div>
        <div className="h-3 w-full bg-white/5 overflow-hidden flex">
          <div className="h-full bg-emerald-500" style={{ width: `${subMix}%` }} />
          <div className="h-full bg-[var(--mw-primary)]" style={{ width: `${100 - Number(subMix)}%` }} />
        </div>
        <div className="mt-3 flex justify-between text-[11px] uppercase tracking-[0.22em] text-white/55">
          <span className="inline-flex items-center gap-2"><span className="w-2 h-2 bg-emerald-500" /> Subscriptions · {subMix}%</span>
          <span className="inline-flex items-center gap-2"><span className="w-2 h-2 bg-[var(--mw-primary)]" /> One-off · {(100 - Number(subMix)).toFixed(1)}%</span>
        </div>
      </div>
    </div>
  );
}
